import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useIntl } from "react-intl";
import styles from "./TranscriptionCaptionOverlay.scss";
import { useTranscription } from "./TranscriptionContext";
import {
  clampCaptionOverlayPosition,
  computeCaptionOverlayPositionBounds
} from "./transcription-caption-overlay-position-utils";

const MAX_VISIBLE_LINES = 3;
const AVATAR_COLORS = ["#1E88E5", "#8E5AE8", "#2AA964", "#E67E22", "#14B8A6", "#EF4444"];

function normalizeSpeakerName(name) {
  if (!name || typeof name !== "string") return "Unknown";
  return name.trim();
}

function getInitials(name) {
  const normalized = normalizeSpeakerName(name);
  const parts = normalized.split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function getAvatarColor(name) {
  const normalized = normalizeSpeakerName(name);
  let hash = 0;

  for (let i = 0; i < normalized.length; i++) {
    hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
  }

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function hasRenderableText(caption) {
  if (!caption) return false;
  if (typeof caption.text === "string") {
    return caption.text.trim().length > 0;
  }
  return Boolean(caption.text);
}

function resolvePositionBounds(containerElement) {
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 1;
  const rect = containerElement?.getBoundingClientRect();

  return computeCaptionOverlayPositionBounds({
    viewportWidth,
    viewportHeight,
    overlayWidth: rect?.width || 0,
    overlayHeight: rect?.height || 0
  });
}

export function TranscriptionCaptionOverlay() {
  const intl = useIntl();
  const {
    isCCEnabled,
    captions,
    currentDisplayName,
    captionOverlayPosition,
    setCaptionOverlayPosition,
    persistCaptionOverlayPosition,
    transcriptionError
  } = useTranscription();
  const positionedContainerRef = useRef(null);
  const dragStateRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const moveCaptionsAriaLabel = intl.formatMessage({
    id: "caption-overlay.move-captions-aria",
    defaultMessage: "Drag to move closed captions"
  });

  const clampPositionToViewport = useCallback(
    position => {
      const bounds = resolvePositionBounds(positionedContainerRef.current);
      return clampCaptionOverlayPosition(position, bounds);
    },
    [positionedContainerRef]
  );

  const endDrag = useCallback(() => {
    dragStateRef.current = null;
    setIsDragging(false);
  }, []);

  const handleDragMove = useCallback(
    event => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      const viewportWidth = typeof window !== "undefined" ? window.innerWidth || 1 : 1;
      const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 1 : 1;
      const bounds = resolvePositionBounds(positionedContainerRef.current);
      const nextPosition = clampCaptionOverlayPosition(
        {
          x: dragState.originPosition.x + (event.clientX - dragState.startClientX) / viewportWidth,
          y: dragState.originPosition.y + (event.clientY - dragState.startClientY) / viewportHeight
        },
        bounds
      );

      setCaptionOverlayPosition(nextPosition);
      event.preventDefault();
      event.stopPropagation();
    },
    [setCaptionOverlayPosition]
  );

  const handleDragStart = useCallback(
    event => {
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      const bounds = resolvePositionBounds(positionedContainerRef.current);
      const originPosition = clampCaptionOverlayPosition(captionOverlayPosition, bounds);
      dragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        originPosition
      };

      setCaptionOverlayPosition(originPosition);
      setIsDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.preventDefault();
      event.stopPropagation();
    },
    [captionOverlayPosition, setCaptionOverlayPosition]
  );

  const handleDragEnd = useCallback(
    event => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      const viewportWidth = typeof window !== "undefined" ? window.innerWidth || 1 : 1;
      const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 1 : 1;
      const bounds = resolvePositionBounds(positionedContainerRef.current);
      const finalPosition = clampCaptionOverlayPosition(
        {
          x: dragState.originPosition.x + (event.clientX - dragState.startClientX) / viewportWidth,
          y: dragState.originPosition.y + (event.clientY - dragState.startClientY) / viewportHeight
        },
        bounds
      );

      setCaptionOverlayPosition(finalPosition);
      persistCaptionOverlayPosition(finalPosition);
      endDrag();
      event.preventDefault();
      event.stopPropagation();
    },
    [endDrag, setCaptionOverlayPosition, persistCaptionOverlayPosition]
  );

  useEffect(() => {
    if (!isCCEnabled || typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => {
      setCaptionOverlayPosition(previous => clampPositionToViewport(previous));
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isCCEnabled, setCaptionOverlayPosition, clampPositionToViewport]);

  const renderableCaptions = captions.filter(hasRenderableText);

  useEffect(() => {
    if (!isCCEnabled || captions.length === 0 || typeof window === "undefined") {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      setCaptionOverlayPosition(previous => clampPositionToViewport(previous));
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isCCEnabled, captions, setCaptionOverlayPosition, clampPositionToViewport]);

  if (!isCCEnabled || (renderableCaptions.length === 0 && !transcriptionError)) {
    return null;
  }

  const visibleCaptions = renderableCaptions.slice(-MAX_VISIBLE_LINES);

  return (
    <div className={styles.captionOverlay}>
      <div
        ref={positionedContainerRef}
        className={styles.positionedContainer}
        style={{
          left: `${(captionOverlayPosition.x * 100).toFixed(2)}%`,
          top: `${(captionOverlayPosition.y * 100).toFixed(2)}%`
        }}
      >
        <div className={styles.dragHandleRow}>
          <button
            type="button"
            className={classNames(styles.dragHandle, { [styles.dragHandleActive]: isDragging })}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
            onLostPointerCapture={handleDragEnd}
            aria-label={moveCaptionsAriaLabel}
            title={moveCaptionsAriaLabel}
          />
        </div>
        <div className={styles.captionsTrack}>
          {transcriptionError ? (
            <div className={styles.captionEntry}>
              <div className={styles.captionErrorBubble} role="alert">
                <span className={styles.captionErrorIcon}>⚠</span>
                <span>{transcriptionError.message}</span>
              </div>
            </div>
          ) : (
            visibleCaptions.map((caption, index) => (
              <div
                key={`${caption.itemId || "caption"}-${caption.contentIndex || 0}-${caption.timestamp || index}-${index}`}
                className={styles.captionEntry}
              >
                <div className={classNames(styles.captionBubble, { [styles.interimText]: !caption.isFinal })}>
                  <span className={styles.speakerInlineBlock}>
                    <span className={styles.avatarInline} style={{ backgroundColor: getAvatarColor(caption.speaker) }}>
                      {getInitials(caption.speaker)}
                    </span>
                    <span className={styles.speakerInline}>
                      {currentDisplayName &&
                      normalizeSpeakerName(caption.speaker).toLowerCase() === currentDisplayName.toLowerCase()
                        ? "You"
                        : normalizeSpeakerName(caption.speaker)}
                      :
                    </span>
                  </span>{" "}
                  <span className={styles.captionTextInline}>{caption.text}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
